import React from "react";
import { DateTime } from "luxon";
import type { FieldValues, UseControllerProps } from "react-hook-form";
import { getOrParseDateTime } from "@cpt/shared";
import type { Plan, ProductPlatform, Tier } from "client/api-v2";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { PlanService } from "../../services/subscriptionService";

type PlanSelectorProps<T extends FieldValues> = UseControllerProps<T> & {
  label?: string;
  required?: boolean;
  platform?: ProductPlatform;
  tier?: Tier;
  allowedTiers?: Tier[];
};

function formatPlanName(plan: Plan): string {
  const parts = [plan.name];

  if (plan.productPlatform) {
    parts.push(`(${plan.productPlatform})`);
  }

  if (plan.tier) {
    parts.push(`[${plan.tier}]`);
  }

  return plan.name;
}

export function PlanSelector<T extends FieldValues>(props: PlanSelectorProps<T>) {
  const { label, required, platform, tier, allowedTiers, ...rest } = props;

  const searchPlans = React.useCallback(() => {
    // UTC, to match the backend's inclusive availability check. Luxon compares by epoch millis, so a local
    // midnight would drop plans available through today for anyone west of UTC.
    const today = DateTime.utc().startOf("day");

    return PlanService.getPlans({
      platform,
      tier,
      pageSize: 100,
    }).then((response) => {
      const filteredItems = (response.items || [])
        .filter((plan) => {
          if (!plan.availableToDate) {
            return true;
          }
          // The axios interceptor turns ISO date strings into luxon DateTimes, so despite the generated
          // type saying `string` the value here may already be a DateTime. Comparing one against a
          // "YYYY-MM-DD" string coerces to NaN, which is never >= anything — that silently hid every plan
          // carrying an availability end date, however far in the future.
          return getOrParseDateTime(plan.availableToDate).startOf("day") >= today;
        })
        // A superseded plan has been replaced and must never be *newly* assigned — the same rule the
        // backend enforces on plan changes and lifecycle transitions. Note this only governs what can be
        // picked; a subscription already sitting on a superseded plan keeps it until someone moves it.
        .filter((plan) => !plan.isSuperseded)
        .filter((plan) => {
          if (!allowedTiers || allowedTiers.length === 0) {
            return true;
          }
          return plan.tier ? allowedTiers.includes(plan.tier) : false;
        });

      return {
        entities: filteredItems,
        count: filteredItems.length,
        totalCount: filteredItems.length,
      };
    });
  }, [platform, tier, allowedTiers]);

  return (
    <EntitySelector
      {...rest}
      label={label || "Plan"}
      apiRequest={searchPlans}
      optionLabelFunc={formatPlanName}
      required={required}
    />
  );
}
